import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {NgCamInitError} from '../domain/ng-cam-init-error';
import {NgCamImage} from '../domain/ng-cam-image';
import {Observable, Subscription, pipe, interval} from 'rxjs';
import { take } from 'rxjs/operators';
import {NgCamUtil} from '../util/ng-cam.util';
import {NgCamMirrorProperties} from '../domain/ng-cam-mirror-properties';
import { CaptureImageError } from '../domain/capture-image-error';
import { NgCamSwitched } from '../domain/ng-cam-switched'

@Component({
  selector: 'ng-cam',
  templateUrl: './ng-cam.component.html',
  styleUrls: ['./ng-cam.component.scss']
})
export class NgCamComponent implements AfterViewInit, OnDestroy {

  /**
   * If the given Observable emits, an image will be captured and emitted through 'imageCapture' EventEmitter
   */
  @Input()
  public set trigger(trigger: Observable<void>) {
    if (this.triggerSubscription) {
      this.triggerSubscription.unsubscribe();
    }

    // Subscribe to events from this Observable to take snapshots
    this.triggerSubscription = trigger.subscribe(() => {
      if (this.ngcamImage) {

        console.warn('你点的太快了,照片正在预览中！');

        this.captureError.next(<CaptureImageError>{message: '你点的太快了,照片正在预览中'});


      } else {

        this.takeSnapshot();

      }
    });
  }

  /**
   * 如果发出给定的Observable，则活动的网络摄像头将切换到由发出的值指示的网络摄像头。
   * If the given Observable emits, the active ngcam will be switched to the one indicated by the emitted value.
   * @param switchCamera Indicates which ngcam to switch to
   *   true: cycle forwards through available ngcams
   *   false: cycle backwards through available ngcams
   *   string: activate the ngcam with the given id
   */
  @Input()
  public set switchCamera(switchCamera: Observable<boolean | string>) {
    if (this.switchCameraSubscription) {
      this.switchCameraSubscription.unsubscribe();
    }

    // Subscribe to events from this Observable to switch video device
    this.switchCameraSubscription = switchCamera.subscribe((value: boolean | string) => {
      if (typeof value === 'string') {
        // deviceId was specified
        this.switchToVideoInput(value);
      } else {
        // direction was specified
        this.rotateVideoInput(value !== false);
      }
    });
  }
  public get ngcamWrapperStyle() {
    if (this.realWidth && this.realHeight) {
      return {
        'width': this.realWidth.toString() + 'px',
        'height': this.realHeight.toString() + 'px'
      };
    }
  }

  public get videoStyle() {
    const scaleX = this.realWidth && this.width ? (this.realWidth / this.width) : this.width ;
    const scaleY = this.realHeight && this.height ? (this.realHeight / this.height) : this.height ;

    if (scaleX !== 1.01 || scaleY !== 1.01) {
      if(scaleX > scaleY){
        return {
          'width': this.width.toString() + 'px',
          'height': this.height.toString() + 'px',
          'transformOrigin': '0px 0px',
          'transform': 'scaleX(' + scaleX + ') scaleY(' + scaleX + ')',
        };
      }else{
        return {
          'width': this.width.toString() + 'px',
          'height': this.height.toString() + 'px',
          'transformOrigin': '0px 0px',
          'transform': 'scaleX(' + scaleY + ') scaleY(' + scaleY + ')',
        };
      }
    } else {
      return {
        'width': this.width.toString() + 'px',
        'height': this.height.toString() + 'px',
      };
    }

  }

  public get imageStyle() {
    const scaleX = this.realWidth && this.width ? (this.realWidth / this.width) : this.width ;
    const scaleY = this.realHeight && this.height ? (this.realHeight / this.height) : this.height ;

    if (scaleX !== 1.01 || scaleY !== 1.01) {
      if(scaleX > scaleY){
        return {
          'width': this.width.toString() + 'px',
          'height': this.height.toString() + 'px',
          'transformOrigin': '0px 0px',
          'transform': 'scaleX(' + scaleX + ') scaleY(' + scaleX + ')',
          'opacity': this.ngcamImage ? '1' : '0',
        };
      }else{
        return {
          'width': this.width.toString() + 'px',
          'height': this.height.toString() + 'px',
          'transformOrigin': '0px 0px',
          'transform': 'scaleX(' + scaleY + ') scaleY(' + scaleY + ')',
          'opacity': this.ngcamImage ? '1' : '0',
        };


      }

    } else {
      return {
        'width': this.width.toString() + 'px',
        'height': this.height.toString() + 'px',
        'opacity': this.ngcamImage ? '1' : '0',
      };
    }

  }


  public get videoStyleClasses() {
    let classes: string = '';

    if (this.isMirrorImage()) {
      classes += 'mirrored ';
    }

    return classes.trim();
  }

  public get nativeVideoElement() {
    return this.video.nativeElement;
  }
  private static DEFAULT_VIDEO_OPTIONS: MediaTrackConstraints = {facingMode: 'environment'};
  private static DEFAULT_IMAGE_TYPE: string = 'image/jpeg';
  private static DEFAULT_IMAGE_QUALITY: number = 0.92;
  private static DEFAULT_IMAGE_ROTATE: number = 0;
  private static DEFAULT_IMAGE_PREVIEW: number = 0;

  /** 定义摄像头分辨率 宽度（以像素为单位） */
  @Input() public width: number = 640;
  /** 定义摄像头分辨率 高度（以像素为单位）*/
  @Input() public height: number = 480;

  /** 定义摄像头区域的实际显示最大宽度（以像素为单位）  */
  @Input() public realWidth: number = 640;
  /** 定义摄像头区域的实际显示最大高度（以像素为单位）*/
  @Input() public realHeight: number = 480;
  /** Defines base constraints to apply when requesting video track from UserMedia */
  @Input() public videoOptions: MediaTrackConstraints = NgCamComponent.DEFAULT_VIDEO_OPTIONS;
  /** 启用/禁用摄像机切换图标。启用后，如果找到多个摄像头，将显示一个开关图标 */
  @Input() public allowCameraSwitch: boolean = true;
  /** Parameter to control image mirroring (i.e. for user-facing camera). ["auto", "always", "never"] */
  @Input() public mirrorImage: string | NgCamMirrorProperties;
  /** Flag to control whether an ImageData object is stored into the NgCamImage object. */
  @Input() public captureImageData: boolean = false;
  /** The image type to use when capturing snapshots */
  @Input() public imageType: string = NgCamComponent.DEFAULT_IMAGE_TYPE;
  /** The image quality to use when capturing snapshots (number between 0 and 1) */
  @Input() public imageQuality: number = NgCamComponent.DEFAULT_IMAGE_QUALITY;
  /** 当使用拍照时，快照图片旋转角度建议值 0 、-90、90、180 */
  @Input() public imageRotate: number = NgCamComponent.DEFAULT_IMAGE_ROTATE;

  /** 当使用拍照时，快照图片覆盖视频部分一定时间进行图片预览 单位为秒 默认0*/
  @Input() public imagePreview: number = NgCamComponent.DEFAULT_IMAGE_PREVIEW;

   /** 用于预览的图片*/
  public ngcamImage: NgCamImage = null;
  public previewCountDown = 0;

  /** EventEmitter which fires when an image has been captured */
  @Output() public imageCapture: EventEmitter<NgCamImage> = new EventEmitter<NgCamImage>();
  /** Emits a mediaError if ngcam cannot be initialized (e.g. missing user permissions) */
  @Output() public initError: EventEmitter<NgCamInitError> = new EventEmitter<NgCamInitError>();

  /** Emits a mediaError if ngcam cannot be initialized (e.g. missing user permissions) */
  @Output() public captureError: EventEmitter<CaptureImageError> = new EventEmitter<CaptureImageError>();

  /** Emits when the ngcam video was clicked */
  @Output() public imageClick: EventEmitter<void> = new EventEmitter<void>();
  /** Emits the active deviceId after the active video device was switched */
  @Output() public cameraSwitched: EventEmitter<NgCamSwitched> = new EventEmitter<NgCamSwitched>();

  /** available video devices */
  public availableVideoInputs: MediaDeviceInfo[] = [];

  /** Indicates whether the video device is ready to be switched */
  public videoInitialized: boolean = false;
  private  mediaDevices: MediaDevices = navigator.mediaDevices;

  /** If the Observable represented by this subscription emits, an image will be captured and emitted through
   * the 'imageCapture' EventEmitter */
  private triggerSubscription: Subscription;
  /** Index of active video in availableVideoInputs */
  private activeVideoInputIndex: number = -1;
  /** Subscription to switchCamera events */
  private switchCameraSubscription: Subscription;
  /** MediaStream object in use for streaming UserMedia data */
  private mediaStream: MediaStream = null;
  @ViewChild('video', { static: true }) private video: any;
  /** Canvas for Video Snapshots */
  @ViewChild('canvasRotate', { static: true }) private canvasRotate: any;

  /** Canvas for Video Snapshots */
  @ViewChild('canvas', { static: true }) private canvas: any;

  /** width and height of the active video stream */
  private activeVideoSettings: MediaTrackSettings = null;

  /**
   * 获取MediaTrackConstraints以请求流式传输给定设备 Get MediaTrackConstraints to request streaming the given device
   * @param deviceId
   * @param baseMediaTrackConstraints base constraints to merge deviceId-constraint into
   * @returns
   */
  private static getMediaConstraintsForDevice(deviceId: string, baseMediaTrackConstraints: MediaTrackConstraints): MediaTrackConstraints {
    const result: MediaTrackConstraints = baseMediaTrackConstraints ? baseMediaTrackConstraints : this.DEFAULT_VIDEO_OPTIONS;
    if (deviceId) {
      result.deviceId = {exact: deviceId};
    }

    return result;
  }

  /**
   * 尝试从给定的mediaStreamTrack对象中获取deviceId。Tries to harvest the deviceId from the given mediaStreamTrack object.
   * Browsers populate this object differently; this method tries some different approaches
   * to read the id.
   * @param mediaStreamTrack
   * @returns deviceId if found in the mediaStreamTrack
   */
  private static getDeviceIdFromMediaStreamTrack(mediaStreamTrack: MediaStreamTrack): string {
    if (mediaStreamTrack.getSettings && mediaStreamTrack.getSettings() && mediaStreamTrack.getSettings().deviceId) {
      return mediaStreamTrack.getSettings().deviceId;
    } else if (mediaStreamTrack.getConstraints && mediaStreamTrack.getConstraints() && mediaStreamTrack.getConstraints().deviceId) {
      const deviceIdObj: ConstrainDOMString = mediaStreamTrack.getConstraints().deviceId;
      return NgCamComponent.getValueFromConstrainDOMString(deviceIdObj);
    }
  }

  /**
   * 尝试从给定的mediaStreamTrack对象中获取FaceingMode。Tries to harvest the facingMode from the given mediaStreamTrack object.
   * Browsers populate this object differently; this method tries some different approaches
   * to read the value.
   * @param mediaStreamTrack
   * @returns facingMode if found in the mediaStreamTrack
   */
  private static getFacingModeFromMediaStreamTrack(mediaStreamTrack: MediaStreamTrack): string {
    if (mediaStreamTrack) {
      if (mediaStreamTrack.getSettings && mediaStreamTrack.getSettings() && mediaStreamTrack.getSettings().facingMode) {
        return mediaStreamTrack.getSettings().facingMode;
      } else if (mediaStreamTrack.getConstraints && mediaStreamTrack.getConstraints() && mediaStreamTrack.getConstraints().facingMode) {
        const facingModeConstraint: ConstrainDOMString = mediaStreamTrack.getConstraints().facingMode;
        return NgCamComponent.getValueFromConstrainDOMString(facingModeConstraint);
      }
    }
  }

  /**
   * 确定给定的mediaStreamTrack是否声称自己是面向用户的Determines whether the given mediaStreamTrack claims itself as user facing
   * @param mediaStreamTrack
   */
  private static isUserFacing(mediaStreamTrack: MediaStreamTrack): boolean {
    const facingMode: string = NgCamComponent.getFacingModeFromMediaStreamTrack(mediaStreamTrack);
    return facingMode ? 'user' === facingMode.toLowerCase() : false;
  }

  /**
   * 从给定的ConstrainDOMString中提取值Extracts the value from the given ConstrainDOMString
   * @param constrainDOMString
   */
  private static getValueFromConstrainDOMString(constrainDOMString: ConstrainDOMString): string {
    if (constrainDOMString) {
      if (constrainDOMString instanceof String) {
        return String(constrainDOMString);
      } else if (Array.isArray(constrainDOMString) && Array(constrainDOMString).length > 0) {
        return String(constrainDOMString[0]);
      } else if (typeof constrainDOMString === 'object') {
        if (constrainDOMString['exact']) {
          return String(constrainDOMString['exact']);
        } else if (constrainDOMString['ideal']) {
          return String(constrainDOMString['ideal']);
        }
      }
    }

    return null;
  }

  public ngAfterViewInit(): void {
    this.detectAvailableDevices()
      .then(() => {
        // start video
        this.switchToVideoInput(null);
      })
      .catch((err: string) => {
        this.initError.next(<NgCamInitError>{message: err});
        // 回退：即使设备枚举失败，仍尝试加载摄像机 fallback: still try to load ngcam, even if device enumeration failed
        this.switchToVideoInput(null);
      });
  }

  public ngOnDestroy(): void {
    this.stopMediaTracks();
    this.unsubscribeFromSubscriptions();
  }

  /**
   * 计算旋转参数
   */
  public calcRotationParams(
    width: number,
    height: number,
    rotate: number,
  ) {

    let w = +width;
    let h = +height;
    let angular = (rotate ? rotate : 0) % 360;

    if (angular === 0) {
      return { w, h, angular };
    }
    if (angular < 0) {
      angular = angular + 360;
    }
    const ratio = angular / 45;

    if (ratio >= 1 && ratio < 3) {
      w = height;
      h = width;
    } else if (ratio >= 5 && ratio < 7) {
      w = height;
      h = width;
    }

    return { w, h, angular };
  }

  drawRotated(cvs: HTMLCanvasElement, ctx: any,  image: HTMLCanvasElement | HTMLImageElement, degrees: number): void {

    if (!ctx) {
      throw new Error('canvas context invalud');
    }

    ctx.clearRect(0, 0, cvs.width, cvs.height);

    // save the unrotated context of the canvas so we can restore it later
    // the alternative is to untranslate & unrotate after drawing
    ctx.save();

    // move to the center of the canvas
    ctx.translate(cvs.width / 2, cvs.height / 2);

    // rotate the canvas to the specified degrees
    ctx.rotate(degrees * Math.PI / 180);

    // draw the image
    // since the context is rotated, the image will be rotated also
    ctx.drawImage(image, -image.width / 2, -image.height / 2);

    // we’re done with the rotating so restore the unrotated context
    ctx.restore();
  }

  /** 输出图像 */
 exportFromCanvas(_canvas: HTMLCanvasElement, context2d: any) {
      // 将画布内容读取为图像
      const mimeType: string = this.imageType ? this.imageType : NgCamComponent.DEFAULT_IMAGE_TYPE;
      const quality: number = this.imageQuality ? this.imageQuality : NgCamComponent.DEFAULT_IMAGE_QUALITY;


      const dataUrl: string = _canvas.toDataURL(mimeType, quality);

      // 从画布的上下文中获取ImageData对象。
      let imageData: ImageData = null;

      if (this.captureImageData) {
        imageData = context2d.getImageData(0, 0, _canvas.width, _canvas.height);
      }

      const ngcamImage = new NgCamImage(dataUrl, mimeType, imageData);

      // 如果需要预览
      if (this.imagePreview > 0) {
        const imagePreviewTime = this.imagePreview * 1000;

        this.ngcamImage = ngcamImage;

        this.imagePreviewCountDown();

        setTimeout(() => {

          this.ngcamImage = null;

         }, imagePreviewTime);



      }

      this.imageCapture.next(ngcamImage);

}

  /** 预览倒计时 */
  imagePreviewCountDown() {

    if (this.imagePreview > 0) {

      this.previewCountDown = this.imagePreview;

      const  count = this.imagePreview - 1;

      const numbers = interval(1000);
      const takeFourNumbers = numbers.pipe(take(count));
      takeFourNumbers.subscribe(
        x => {
          this.previewCountDown = count - x;
        },
        error => {},
        () => {
          this.previewCountDown = 1;
        });
    }
  }



  /**
   * 拍摄当前摄像头视图的快照并将图像作为事件发送Takes a snapshot of the current ngcam's view and emits the image as an event
   */
  public takeSnapshot(): void {

    const _video = this.nativeVideoElement;

    if (_video) {

      // 将画布大小设置为实际视频大小
      const dimensions = {width: this.width, height: this.height};

      // 旋转度
      let rotate: number = this.imageRotate;

      if (typeof rotate === 'undefined' || rotate === null) {
        rotate = NgCamComponent.DEFAULT_IMAGE_ROTATE;
      }

      const { w, h, angular } = this.calcRotationParams(dimensions.width, dimensions.height, rotate);

      const _canvas = this.canvas.nativeElement;

      _canvas.width = w;
      _canvas.height = h;

      const context2d = _canvas.getContext('2d');

      // 是否需要旋转
      if (angular === 0) {// 不旋转

        // 将快照图像绘制到画布
        context2d.drawImage(_video, 0, 0);

        this.exportFromCanvas(_canvas, context2d);

      } else {// 旋转

        // 将快照图像绘制到画布canvasRotate以备旋转
        const _canvasRotate = this.canvasRotate.nativeElement;

        _canvasRotate.width = dimensions.width;
        _canvasRotate.height = dimensions.height;

        const ctx1 = <CanvasRenderingContext2D> _canvasRotate.getContext('2d');

        ctx1.drawImage(_video, 0, 0, dimensions.width, dimensions.height);

        this.drawRotated(_canvas, context2d, _canvasRotate, angular);    // rotate image

        _canvasRotate.width = _canvasRotate.height = 0;

        this.exportFromCanvas(_canvas, context2d);

      }

    } else {
      this.captureError.next(<CaptureImageError>{message: 'video empty 无法截图'});
    }

  }

  /**
   * 切换到下一个/上一个视频设备
   * @param forward
   */
  public rotateVideoInput(forward: boolean) {
    if (this.availableVideoInputs && this.availableVideoInputs.length > 1) {
      const increment: number = forward ? 1 : (this.availableVideoInputs.length - 1);
      const nextInputIndex = (this.activeVideoInputIndex + increment) % this.availableVideoInputs.length;
      this.switchToVideoInput(this.availableVideoInputs[nextInputIndex].deviceId);
    }
  }

  /**
   * 将摄像机视图切换到指定的视频设备
   */
  public switchToVideoInput(deviceId: string): void {
    this.videoInitialized = false;

    if(!this.videoOptions.width){

      this.videoOptions.width = {
        ideal: Math.floor(this.width),
        min: Math.floor(this.width * 0.9),
      }
    }

    if(!this.videoOptions.height){

      this.videoOptions.height = {
        ideal: Math.floor(this.height),
        min: Math.floor(this.height * 0.9),
      }
    }

    // 视频设备切换不在停止设备连接，避免设备切换缓慢
    // this.stopMediaTracks();
    this.initNgCam(deviceId, this.videoOptions);
  }


  /**
   * 视频调整大小事件的事件处理程序。
   * 触发角度变化检测，以便应用新的视频维度Triggers Angular change detection so that new video dimensions get applied
   */
  public videoResize(): void {
    // 这里触发角度变化检测here to trigger Angular change detection
  }

  /**
   * Init ngcam live view 初始化摄像头
   */
  private initNgCam(deviceId: string, userVideoTrackConstraints: MediaTrackConstraints) {
    const _video = this.nativeVideoElement;

    if (this.mediaDevices && this.mediaDevices.getUserMedia) {

      // merge deviceId -> userVideoTrackConstraints
      const videoTrackConstraints = NgCamComponent.getMediaConstraintsForDevice(deviceId, userVideoTrackConstraints);

      this.mediaDevices.getUserMedia(<MediaStreamConstraints>{audio: false, video: videoTrackConstraints})
        .then((stream: MediaStream) => {
          this.mediaStream = stream;
          _video.srcObject = stream;
          _video.play();

          this.activeVideoSettings = stream.getVideoTracks()[0].getSettings();
          const activeDeviceId: string = NgCamComponent.getDeviceIdFromMediaStreamTrack(stream.getVideoTracks()[0]);

          this.cameraSwitched.next({
            deviceId:activeDeviceId,
            video:_video
          });

          // Initial detect may run before user gave permissions, returning no deviceIds. This prevents later camera switches. (#47)
          // Run detect once again within getUserMedia callback, to make sure this time we have permissions and get deviceIds.
          this.detectAvailableDevices()
            .then(() => {
              this.activeVideoInputIndex = activeDeviceId ? this.availableVideoInputs
                .findIndex((mediaDeviceInfo: MediaDeviceInfo) => mediaDeviceInfo.deviceId === activeDeviceId) : -1;
              this.videoInitialized = true;
            })
            .catch(() => {
              this.activeVideoInputIndex = -1;
              this.videoInitialized = true;
            });
        })
        .catch((err: MediaStreamError) => {
          this.initError.next(<NgCamInitError>{message: err.message, mediaStreamError: err});
        });
    } else {
      this.initError.next(<NgCamInitError>{message: 'Cannot read UserMedia from MediaDevices.'});
    }
  }

  private getActiveVideoTrack(): MediaStreamTrack {
    return this.mediaStream ? this.mediaStream.getVideoTracks()[0] : null;
  }

  private isMirrorImage(): boolean {
    if (!this.getActiveVideoTrack()) {
      return false;
    }

    // check for explicit mirror override parameter
    {
      let mirror: string = 'auto';
      if (this.mirrorImage) {
        if (typeof this.mirrorImage === 'string') {
          mirror = String(this.mirrorImage).toLowerCase();
        } else {
          // NgCamMirrorProperties
          if (this.mirrorImage.x) {
            mirror = this.mirrorImage.x.toLowerCase();
          }
        }
      }

      switch (mirror) {
        case 'always':
          return true;
        case 'never':
          return false;
      }
    }

    // default: enable mirroring if ngcam is user facing
    return NgCamComponent.isUserFacing(this.getActiveVideoTrack());
  }

  /**
   * Stops all active media tracks.
   * This prevents the ngcam from being indicated as active,
   * even if it is no longer used by this component.
   */
  private stopMediaTracks() {
    if (this.mediaStream && this.mediaStream.getTracks) {
      // getTracks() returns all media tracks (video+audio)
      this.mediaStream.getTracks()
        .forEach((track: MediaStreamTrack) => track.stop());
    }
  }

  /**
   * Unsubscribe from all open subscriptions
   */
  private unsubscribeFromSubscriptions() {
    if (this.triggerSubscription) {
      this.triggerSubscription.unsubscribe();
    }
    if (this.switchCameraSubscription) {
      this.switchCameraSubscription.unsubscribe();
    }
  }

  /**
   * Reads available input devices
   */
  private detectAvailableDevices(): Promise<MediaDeviceInfo[]> {
    return new Promise((resolve, reject) => {
      NgCamUtil.getAvailableVideoInputs()
        .then((devices: MediaDeviceInfo[]) => {
          this.availableVideoInputs = devices;
          resolve(devices);
        })
        .catch(err => {
          this.availableVideoInputs = [];
          reject(err);
        });
    });
  }

}
